import { useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, Loader2 } from 'lucide-react';

import { fetchCandidateManifest, selectCandidate, type CandidateEntry } from '@/lib/api';
import { canonKey } from '@/lib/assetKeys';

/**
 * "Options" affordance on a location / prop / storyboard tile: opens the retained
 * candidate slate (from location_candidates.json, prop_candidates.json or
 * keyframe_candidates.json) so the producer can see every take and swap the canonical
 * to a different one AFTER the phase completed. The swap hits POST
 * /api/select-candidate (the service applies it and invalidates any dependent frames);
 * promoting a keyframe take also re-shoots that shot's clip.
 */
export default function CandidatePicker({
  productionId,
  assetType,
  assetName,
  onSwapped,
  vertical,
}: {
  productionId?: string;
  assetType: 'location' | 'prop' | 'keyframe';
  assetName: string;
  onSwapped: (name: string) => void;
  vertical?: boolean;
}) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(false);
  const [entry, setEntry] = useState<CandidateEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [err, setErr] = useState('');

  const toggle = async (e: ReactMouseEvent) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (entry || !productionId) return;
    setLoading(true);
    try {
      const manifest = await fetchCandidateManifest(productionId, assetType);
      const found = Object.values(manifest).find((v) => canonKey(v.name) === canonKey(assetName)) || null;
      setEntry(found);
    } finally {
      setLoading(false);
    }
  };

  const pick = async (idx: number) => {
    if (!productionId || !entry || busy !== null) return;
    if (idx === entry.chosen_index) {
      setOpen(false);
      return;
    }
    setBusy(idx);
    setErr('');
    try {
      const resp = await selectCandidate(productionId, {
        asset_type: assetType,
        asset_id: entry.name,
        candidate_index: idx,
      });
      if (!resp.ok) {
        const j = (await resp.json().catch(() => ({}))) as { detail?: string };
        setErr(j.detail || t('tile.swapFailed'));
        return;
      }
      setEntry({ ...entry, chosen_index: idx, chosen_by: 'human' });
      onSwapped(assetName);
      setOpen(false);
    } finally {
      setBusy(null);
    }
  };

  const count = entry?.candidates?.length ?? 0;

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={toggle}
        title={t('tile.optionsTitle')}
        className="flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-medium text-white hover:bg-primary"
      >
        <LayoutGrid className="h-2.5 w-2.5" />
        {t('tile.options')}
      </button>
      {open && (
        <div className="absolute left-0 top-6 z-30 w-72 rounded-md border border-border bg-card p-2 shadow-xl">
          {loading && (
            <div className="flex items-center gap-1 p-2 text-[10px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> {t('tile.optionsLoading')}
            </div>
          )}
          {!loading && (!entry || count <= 1) && (
            <div className="p-2 text-[10px] text-muted-foreground">{t('tile.optionsNone')}</div>
          )}
          {!loading && entry && count > 1 && (
            <>
              <div className="mb-1 text-[10px] text-muted-foreground">{t(`tile.optionsWarn.${assetType}`)}</div>
              <div className="grid grid-cols-3 gap-1">
                {entry.candidates
                  .slice()
                  .sort((a, b) => a.index - b.index)
                  .map((c) => {
                    const isCur = c.index === entry.chosen_index;
                    return (
                      <button
                        key={c.index}
                        type="button"
                        onClick={() => pick(c.index)}
                        disabled={busy !== null}
                        className={
                          'relative overflow-hidden rounded border-2 ' +
                          (isCur ? 'border-primary' : 'border-transparent hover:border-primary/50')
                        }
                        title={t('tile.optionsTake', {
                          number: c.index + 1,
                          score: c.score.toFixed(2),
                          defaultValue: `Take ${c.index + 1} · score ${c.score.toFixed(2)}`,
                        })}
                      >
                        <img
                          src={`/api/assets/${productionId}/${c.path}?thumb=1`}
                          alt={`take ${c.index + 1}`}
                          className={`${vertical ? 'aspect-[9/16]' : 'aspect-video'} w-full object-cover`}
                        />
                        {isCur && (
                          <span className="absolute left-0 top-0 bg-primary px-0.5 text-[7px] font-bold text-primary-foreground">
                            {t('tile.optionsCurrent')}
                          </span>
                        )}
                        {busy === c.index && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                            <Loader2 className="h-3 w-3 animate-spin text-white" />
                          </div>
                        )}
                        <span className="absolute bottom-0 right-0 bg-black/70 px-0.5 text-[7px] text-white">
                          {c.score.toFixed(2)}
                        </span>
                      </button>
                    );
                  })}
              </div>
              {err && <div className="mt-1 text-[10px] text-destructive">{err}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
